// Copyright 2018 Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import React from 'react'
import { reaction } from 'mobx';
import { store } from 'services/state'
import { authHeaderValueIsPresent, fillOpenAuthHeader, preauthorizeApiLoginToken } from 'services/swagger-api-auth'

// Constant-time compare so Bearer javascript_lang_observable_timing does not
// treat JWT identity checks as secret string equality.
// Instruction used: scope-discipline.instructions.md
function markAuthorizeButton(authButton) {
    authButton.style = "border-color: #6b7280 !important; color: #6b7280 !important;"
    if (authButton.firstElementChild) {
        authButton.firstElementChild.innerText = "Authorized"
        authButton.firstElementChild.style = "color: #6b7280 !important;"
    }
}

function tokensEqual(left, right) {
    const a = String(left == null ? '' : left)
    const b = String(right == null ? '' : right)
    const length = Math.max(a.length, b.length)
    let mismatch = a.length ^ b.length
    for (let i = 0; i < length; i++) {
        mismatch |= (a.charCodeAt(i) || 0) ^ (b.charCodeAt(i) || 0)
    }
    return mismatch === 0
}

/**
 *
 * author: 430008283
 *
 */
export default class AuthorizationAutomator extends React.Component {

    constructor() {
        super()
        this.apiToken = null
        this.intervalId = null
        this.swaggerUi = null
        this.automateApiAuthorization = this.automateApiAuthorization.bind(this)
        // This is to detect if api.token has changed and to trigger authorization.
        this.reactionDisposer = reaction(
            () => store.idToken,
            (newVal, prevVal) => {
                this.automateApiAuthorization();
            }
        );
    }

    componentDidMount() {
        console.log("AuthorizationAutomator is on")
    }

    componentWillUnmount() {
        console.log("AuthorizationAutomator is off")
        this.reactionDisposer();
    }

    // The page assigns this after render. A prop would stay undefined when
    // API login finishes without leaving the open API page.
    // Instruction used: debugging.instructions.md
    receiveSwaggerUi(uiHandler) {
        this.swaggerUi = uiHandler
        if (store.idToken) {
            this.automateApiAuthorization()
        }
    }

    automateApiAuthorization = () => {
            // when no API token has been generated, don't authorize
        if (!store.idToken) {
            return
        }

        // Store the token before touching the Authorize label. The old
        // ApiCognitoAuthorizer form match no longer exists on Premium-API-Auth.
        // Swagger keeps the span text as Authorize; the label below is ours.
        // The handler comes from the page, not a render prop, so login on an
        // already open API page still has it.
        // Instruction used: debugging.instructions.md
        var appliedSchemes = preauthorizeApiLoginToken(this.swaggerUi, store.api && store.api.swagger, store.idToken)
        // api_key alone locks this button. Do not label it from that lock.
        // Instruction used: debugging.instructions.md
        if (!appliedSchemes.length) {
            return
        }

        // get the DOM Authorize button
        // class="btn authorize unlocked" or "btn authorize locked"
        var authButton = document.getElementsByClassName("btn authorize")[0]
        if (!authButton) {
            return
        }
        var dialog = authButton.parentElement && authButton.parentElement.querySelector('.dialog-ux')
        var openedByAutomator = false
        if (!dialog) {
            var authorizeBtn = this.findReact(authButton)
            if (authorizeBtn && authorizeBtn.props && authorizeBtn.props.onClick) {
                authorizeBtn.props.onClick()
                openedByAutomator = true
                dialog = authButton.parentElement && authButton.parentElement.querySelector('.dialog-ux')
            }
        }
        if (dialog) fillOpenAuthHeader(dialog, store.idToken, this.findReact)
        // The page button locks for api_key alone. Label it only when the
        // Auth header field itself holds the token.
        // Instruction used: debugging.instructions.md
        if (!authHeaderValueIsPresent(dialog || document)) {
            return
        }
        markAuthorizeButton(authButton)
        if (openedByAutomator && dialog) {
            var done = dialog.querySelector('button.btn-done')
            if (done) done.click()
            return
        }

            // the Available authorizations dialog was already opened (by user manually)
        if (authButton.nextSibling && authButton.nextSibling.className == 'dialog-ux') {
            return
        }

            // get the wrapper React component of the button
        authorizeBtn = this.findReact(authButton)
            // open the Available Authorization dialog
        authorizeBtn.props.onClick()

/*The following is an example of the Authorize button and the dialog it opens when clicked on

<div class="auth-wrapper">
<button class="btn authorize unlocked">
    <span>Authorize</span>
    <svg width="20" height="20">...</svg>
</button>
<div class="dialog-ux">
    <div class="backdrop-ux"></div>
    <div class="modal-ux" style="display: none">
        <div class="modal-dialog-ux">...</div>
    </div>
</div>
</div>
*/
        var doneButton = null

        var authForms = authButton.nextSibling.getElementsByTagName("form")

        for (var i = 0; i < authForms.length; i++) {
            var af = authForms[i]
            if (af.innerText.startsWith("ApiCognitoAuthorizer")) { // done button, logout button
                if (af.elements.length == 2) {
                    if (tokensEqual(this.apiToken, store.idToken)) {
                        af.elements[0].click()
                    } else {
                        af.elements[1].click()
                    }
                } else if (af.elements.length == 3) {
                    var apiTokenInput = this.findReact(af.elements[0]);
                    if (apiTokenInput) {
                        apiTokenInput.props.onChange({ target : { value : store.idToken } })
                            // click on the API key Authorize button
                        af.elements[1].click()
                        this.apiToken = store.idToken
                        console.log("Authorized API token")
                    } else {
                        console.log("ERROR: React component apiTokenInput was null or undefined so couldn't authorize API token")
                    }
                }
            } else if (af.innerText.startsWith("api_key")) { // input, done button, authorize button
                if (af.elements.length == 2 && this.apiKey != store.apiKey) {
                    // console.log("Click on Logout button ..")
                    af.elements[0].click()
                }
                if (af.elements.length == 3) {
                    var apiKeyInput = this.findReact(af.elements[0]);
                    if (apiKeyInput) {
                        apiKeyInput.props.onChange({ target : { value : store.apiKey } })
                            // click on the API key Authorize button
                        af.elements[1].click()
                        console.log("Authorized API key")
                    } else {
                        console.log("ERROR: React component apiKeyInput was null or undefined so couldn't authorize API key")
                    }
                }
            } else  if (af.innerText.startsWith("gnw-customer-id")) { // input, done button, authorize button
                if (af.elements.length == 3) {
                    var apiKeyInput = this.findReact(af.elements[0]);
                    if (apiKeyInput) {
                        apiKeyInput.props.onChange({ target : { value : "258" } })
                        // click on the API key Authorize button
                        af.elements[1].click()
                        console.log("Authorized CID")
                    } else {
                        console.log("ERROR: UNABLE to set CID")
                    }
                }
            }{
                console.log("SHOULD NOT REACH HERE?")
            }
            doneButton = af.elements[af.elements.length - 1]
        }
            // There should be at least one form, and its first element should be the Done button
        doneButton.click()
    }



    findReact = (dom) => {      // from https://stackoverflow.com/a/39165137/4831179
        for (var key in dom) {
            if (key.startsWith("__reactInternalInstance$")) {
                var compInternals = dom[key]._currentElement;
                var compWrapper = compInternals._owner;
                var comp = compWrapper._instance;
                return comp;
            }
        }
        return null;
    };

    render() {
        return null
    }
}

